import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  deleteDoc,
  updateDoc,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import { localDateKey, localDateKeys } from '../utils/dateKey';

// ── Workouts ──────────────────────────────────────────────────────────────────

export async function fsGetWorkouts(uid) {
  if (!uid || !db) return null;
  const q = query(collection(db, 'users', uid, 'workouts'), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function fsSaveWorkout(uid, workout) {
  if (!uid || !db) return null;
  const id = workout.id || String(Date.now());
  const entry = { ...workout, id, createdAt: workout.createdAt || new Date().toISOString() };
  await setDoc(doc(db, 'users', uid, 'workouts', id), entry);
  return entry;
}

export async function fsDeleteWorkout(uid, id) {
  if (!uid || !db) return;
  await deleteDoc(doc(db, 'users', uid, 'workouts', id));
}

// ── Gym Schedule ──────────────────────────────────────────────────────────────

export async function fsGetSchedule(uid) {
  if (!uid || !db) return null;
  const snap = await getDocs(collection(db, 'users', uid, 'schedule'));
  if (snap.empty) return null;
  const schedule = {};
  snap.docs.forEach((d) => {
    schedule[d.id] = d.data();
  });
  return schedule;
}

export async function fsSaveSchedule(uid, schedule) {
  if (!uid || !db) return;
  const writes = Object.entries(schedule).map(([day, data]) =>
    setDoc(doc(db, 'users', uid, 'schedule', day), data)
  );
  await Promise.all(writes);
}

// ── Completions ───────────────────────────────────────────────────────────────

/**
 * Toggle a single exercise for the given dayKey on today's date.
 * Returns the updated array of completed exercises for that day.
 */
export async function fsToggleExercise(uid, dayKey, exercise) {
  if (!uid || !db) return null;
  const today = new Date().toISOString().split('T')[0];
  const ref = doc(db, 'users', uid, 'completions', today);
  const snap = await getDoc(ref);
  const data = snap.exists() ? snap.data() : {};
  const current = data[dayKey] || [];
  const updated = current.includes(exercise)
    ? current.filter((e) => e !== exercise)
    : [...current, exercise];
  await setDoc(ref, { ...data, [dayKey]: updated }, { merge: true });
  return updated;
}

export async function fsGetTodayCompletions(uid, dayKey) {
  if (!uid || !db) return null;
  const today = new Date().toISOString().split('T')[0];
  const snap = await getDoc(doc(db, 'users', uid, 'completions', today));
  if (!snap.exists()) return [];
  return snap.data()[dayKey] || [];
}

// ── Steps ─────────────────────────────────────────────────────────────────────

export async function fsGetTodaySteps(uid) {
  if (!uid || !db) return null;
  const today = new Date().toISOString().split('T')[0];
  const snap = await getDoc(doc(db, 'users', uid, 'steps', today));
  return snap.exists() ? (snap.data().count ?? 0) : 0;
}

export async function fsLogSteps(uid, count) {
  if (!uid || !db) return;
  const today = new Date().toISOString().split('T')[0];
  await setDoc(doc(db, 'users', uid, 'steps', today), { count });
}

export async function fsGetWeekSteps(uid) {
  if (!uid || !db) return null;
  const now = new Date();
  const dates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(now);
    d.setDate(d.getDate() - (6 - i));
    return {
      date: d.toISOString().split('T')[0],
      day: d.toLocaleDateString('en-GB', { weekday: 'short' }),
    };
  });

  const snaps = await Promise.all(
    dates.map(({ date }) => getDoc(doc(db, 'users', uid, 'steps', date)))
  );

  return dates.map(({ date, day }, i) => ({
    date,
    day,
    steps: snaps[i].exists() ? (snaps[i].data().count ?? 0) : 0,
  }));
}

export async function fsGetStepGoal(uid) {
  if (!uid || !db) return null;
  const snap = await getDoc(doc(db, 'users', uid, 'preferences'));
  if (!snap.exists()) return null;
  const goal = snap.data().stepGoal;
  return goal != null ? goal : null;
}

export async function fsSaveStepGoal(uid, goal) {
  if (!uid || !db) return;
  await setDoc(doc(db, 'users', uid, 'preferences'), { stepGoal: goal }, { merge: true });
}

// ── Sport Sessions ─────────────────────────────────────────────────────────────

export async function fsGetUpcomingSessions(uid) {
  if (!uid || !db) return null;
  const today = new Date().toISOString().split('T')[0];
  const snap = await getDocs(collection(db, 'users', uid, 'sport_sessions'));
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((s) => s.date >= today)
    .sort((a, b) => a.date.localeCompare(b.date));
}

export async function fsGetAllSportSessions(uid) {
  if (!uid || !db) return null;
  const snap = await getDocs(collection(db, 'users', uid, 'sport_sessions'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function fsSaveSportSession(uid, session) {
  if (!uid || !db) return null;
  const id = session.id || String(Date.now());
  const entry = { ...session, id, createdAt: session.createdAt || new Date().toISOString() };
  await setDoc(doc(db, 'users', uid, 'sport_sessions', id), entry);
  return entry;
}

export async function fsDeleteSportSession(uid, id) {
  if (!uid || !db) return;
  await deleteDoc(doc(db, 'users', uid, 'sport_sessions', id));
}

export async function fsToggleSportSession(uid, id) {
  if (!uid || !db) return;
  const ref = doc(db, 'users', uid, 'sport_sessions', id);
  const snap = await getDoc(ref);
  if (snap.exists()) {
    await updateDoc(ref, { completed: !snap.data().completed });
  }
}

// ── Custom Sports ────────────────────────────────────────────────────────────

export async function fsGetCustomSports(uid) {
  if (!uid || !db) return null;
  const snap = await getDocs(collection(db, 'users', uid, 'custom_sports'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function fsSaveCustomSport(uid, sport) {
  if (!uid || !db) return null;
  const id = sport.id || `custom_${Date.now()}`;
  const entry = { ...sport, id, createdAt: sport.createdAt || new Date().toISOString() };
  await setDoc(doc(db, 'users', uid, 'custom_sports', id), entry);
  return entry;
}

export async function fsDeleteCustomSport(uid, id) {
  if (!uid || !db) return;
  await deleteDoc(doc(db, 'users', uid, 'custom_sports', id));
}

// ── Water Intake ──────────────────────────────────────────────────────────────

export async function fsGetWater(uid) {
  if (!uid || !db) return null;
  const today = localDateKey();
  const [dailySnap, prefsSnap] = await Promise.all([
    getDoc(doc(db, 'users', uid, 'water', today)),
    getDoc(doc(db, 'users', uid, 'preferences', 'health')),
  ]);
  if (!dailySnap.exists() && !prefsSnap.exists()) return null;
  const daily = dailySnap.exists() ? dailySnap.data() : {};
  const prefs = prefsSnap.exists() ? prefsSnap.data() : {};
  const amountMl = Number.isFinite(daily.amountMl)
    ? Math.max(0, Math.round(daily.amountMl))
    : Math.max(0, Math.round((daily.glasses || 0) * 250));
  const goalMl = Number.isFinite(prefs.waterGoalMl)
    ? Math.max(250, Math.round(prefs.waterGoalMl))
    : Number.isFinite(daily.goalMl)
      ? Math.max(250, Math.round(daily.goalMl))
      : Math.max(250, Math.round((daily.goal || prefs.waterGoal || 8) * 250));
  return {
    ...daily,
    hasDaily: dailySnap.exists(),
    hasGoal: Number.isFinite(prefs.waterGoalMl) ||
      Number.isFinite(daily.goalMl) || Number.isFinite(daily.goal) ||
      Number.isFinite(prefs.waterGoal),
    amountMl,
    glasses: amountMl / 250,
    goalMl,
    goal: goalMl / 250,
  };
}

export async function fsLogWater(uid, amountMl) {
  if (!uid || !db) return;
  const today = localDateKey();
  const safeAmount = Math.min(100000, Math.max(0, Math.round(amountMl)));
  await setDoc(doc(db, 'users', uid, 'water', today), {
    amountMl: safeAmount,
    glasses: safeAmount / 250,
    timezoneOffsetMinutes: -new Date().getTimezoneOffset(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function fsSetWaterGoal(uid, goalMl) {
  if (!uid || !db) return;
  const safeGoal = Math.min(20000, Math.max(250, Math.round(goalMl)));
  const today = localDateKey();
  await Promise.all([
    setDoc(doc(db, 'users', uid, 'preferences', 'health'), {
      waterGoalMl: safeGoal,
      waterGoal: safeGoal / 250,
      updatedAt: serverTimestamp(),
    }, { merge: true }),
    setDoc(doc(db, 'users', uid, 'water', today), {
      goalMl: safeGoal,
      goal: safeGoal / 250,
    }, { merge: true }),
  ]);
}

export async function fsLogCaffeine(uid, mg) {
  if (!uid || !db) return;
  const today = localDateKey();
  const ref = doc(db, 'users', uid, 'water', today);
  const snap = await getDoc(ref);
  const current = snap.exists() && Number.isFinite(snap.data().caffeineMg) ? snap.data().caffeineMg : 0;
  const next = Math.max(0, Math.min(2000, current + mg));
  await setDoc(ref, { caffeineMg: next, updatedAt: serverTimestamp() }, { merge: true });
  return next;
}

export async function fsGetWeekWater(uid) {
  if (!uid || !db) return null;
  const dates = localDateKeys(7).map((date) => ({
    date,
    day: new Date(`${date}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short' }),
  }));
  const snaps = await Promise.all(
    dates.map(({ date }) => getDoc(doc(db, 'users', uid, 'water', date)))
  );
  return dates.map(({ date, day }, i) => {
    const data = snaps[i].exists() ? snaps[i].data() : {};
    const amountMl = Number.isFinite(data.amountMl)
      ? Math.max(0, Math.round(data.amountMl))
      : Math.max(0, Math.round((data.glasses || 0) * 250));
    return { date, day, amountMl, glasses: amountMl / 250 };
  });
}

// ── Weight tracking ───────────────────────────────────────────────────────────

export async function fsGetWeight(uid) {
  if (!uid || !db) return null;
  const today = new Date().toISOString().split('T')[0];
  const snap = await getDoc(doc(db, 'users', uid, 'weight', today));
  return snap.exists() ? snap.data() : null;
}

export async function fsLogWeight(uid, weight, unit) {
  if (!uid || !db) return;
  const today = new Date().toISOString().split('T')[0];
  await setDoc(doc(db, 'users', uid, 'weight', today), { weight, unit });
}

export async function fsGetWeekWeight(uid) {
  if (!uid || !db) return null;
  const now = new Date();
  const dates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(now);
    d.setDate(d.getDate() - (6 - i));
    return {
      date: d.toISOString().split('T')[0],
      day: d.toLocaleDateString('en-GB', { weekday: 'short' }),
    };
  });
  const snaps = await Promise.all(
    dates.map(({ date }) => getDoc(doc(db, 'users', uid, 'weight', date)))
  );
  return dates.map(({ date, day }, i) => ({
    date,
    day,
    weight: snaps[i].exists() ? (snaps[i].data().weight ?? null) : null,
  }));
}

export async function fsGetWeightPrefs(uid) {
  if (!uid || !db) return null;
  const snap = await getDoc(doc(db, 'users', uid, 'preferences'));
  if (!snap.exists()) return null;
  const d = snap.data();
  if (d.height == null && d.weightUnit == null) return null;
  return { height: d.height ?? null, weightUnit: d.weightUnit ?? null };
}

export async function fsSaveWeightPrefs(uid, prefs) {
  if (!uid || !db) return;
  await setDoc(doc(db, 'users', uid, 'preferences'), prefs, { merge: true });
}

// ── Body Measurements ─────────────────────────────────────────────────────────

const MEASUREMENT_FIELDS = ['waist', 'chest', 'arms', 'neck', 'hips', 'thigh'];

export async function fsSaveMeasurement(uid, date, values) {
  if (!uid || !db) return;
  const safeValues = {};
  MEASUREMENT_FIELDS.forEach((f) => {
    if (Number.isFinite(values[f])) safeValues[f] = Math.max(0, values[f]);
  });
  await setDoc(doc(db, 'users', uid, 'measurements', date), {
    ...safeValues,
    unit: values.unit || 'cm',
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function fsGetMeasurementHistory(uid, limitCount = 60) {
  if (!uid || !db) return null;
  const snap = await getDocs(collection(db, 'users', uid, 'measurements'));
  return snap.docs
    .map((d) => ({ date: d.id, ...d.data() }))
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(-limitCount);
}

// ── Calorie Counter ───────────────────────────────────────────────────────────

export async function fsGetCalories(uid) {
  if (!uid || !db) return null;
  const today = localDateKey();
  const [dailySnap, prefsSnap] = await Promise.all([
    getDoc(doc(db, 'users', uid, 'calories', today)),
    getDoc(doc(db, 'users', uid, 'preferences', 'health')),
  ]);
  if (!dailySnap.exists() && !prefsSnap.exists()) return null;
  const daily = dailySnap.exists() ? dailySnap.data() : {};
  const preferences = prefsSnap.exists() ? prefsSnap.data() : {};
  return {
    ...daily,
    hasDaily: dailySnap.exists(),
    hasGoal: Number.isFinite(preferences.calorieGoal) ||
      Number.isFinite(daily.goal),
    entries: Array.isArray(daily.entries) ? daily.entries : [],
    goal: Number.isFinite(preferences.calorieGoal)
      ? preferences.calorieGoal
      : (daily.goal ?? 2000),
  };
}

export async function fsLogCalorieEntry(uid, entry) {
  if (!uid || !db) return;
  const today = localDateKey();
  const ref = doc(db, 'users', uid, 'calories', today);
  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(ref);
    const existing = snap.exists() && Array.isArray(snap.data().entries)
      ? snap.data().entries
      : [];
    if (existing.some((item) => item.id === entry.id)) return;
    transaction.set(ref, {
      entries: [...existing, entry],
      date: today,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  });
}

export async function fsDeleteCalorieEntry(uid, entryId) {
  if (!uid || !db) return;
  const today = localDateKey();
  const ref = doc(db, 'users', uid, 'calories', today);
  await runTransaction(db, async (transaction) => {
    const snap = await transaction.get(ref);
    if (!snap.exists()) return;
    const entries = (snap.data().entries || []).filter((e) => e.id !== entryId);
    transaction.set(ref, { entries, updatedAt: serverTimestamp() }, { merge: true });
  });
}

export async function fsSetCalorieGoal(uid, goal) {
  if (!uid || !db) return;
  const today = localDateKey();
  const safeGoal = Math.min(20000, Math.max(1, Math.round(goal)));
  await Promise.all([
    setDoc(doc(db, 'users', uid, 'preferences', 'health'), {
      calorieGoal: safeGoal,
      updatedAt: serverTimestamp(),
    }, { merge: true }),
    setDoc(doc(db, 'users', uid, 'calories', today), { goal: safeGoal }, { merge: true }),
  ]);
}

export async function fsGetWeekCalories(uid) {
  if (!uid || !db) return null;
  const dates = localDateKeys(7).map((date) => ({
    date,
    day: new Date(`${date}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short' }),
  }));
  const snaps = await Promise.all(
    dates.map(({ date }) => getDoc(doc(db, 'users', uid, 'calories', date)))
  );
  return dates.map(({ date, day }, i) => {
    const data = snaps[i].exists() ? snaps[i].data() : {};
    const entries = Array.isArray(data.entries) ? data.entries : [];
    const totalCalories = entries.reduce((s, e) => s + (Number(e.calories) || 0), 0);
    return { date, day, totalCalories };
  });
}

// ── Meal planning ─────────────────────────────────────────────────────────────

export async function fsGetMealPlan(uid) {
  if (!uid || !db) return null;
  const snap = await getDoc(doc(db, 'users', uid, 'meal_plans', 'current'));
  if (!snap.exists()) return null;
  const plan = snap.data().plan;
  return plan && typeof plan === 'object' ? plan : {};
}

export async function fsSaveMealPlan(uid, plan) {
  if (!uid || !db) return;
  await setDoc(doc(db, 'users', uid, 'meal_plans', 'current'), {
    plan,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

// ── Meal ingredients (structured, additive to the freeform meal plan) ─────────

export async function fsGetMealIngredients(uid) {
  if (!uid || !db) return null;
  const snap = await getDoc(doc(db, 'users', uid, 'meal_plans', 'ingredients'));
  if (!snap.exists()) return null;
  const data = snap.data().byDay;
  return data && typeof data === 'object' ? data : {};
}

export async function fsSaveMealIngredients(uid, byDay) {
  if (!uid || !db) return;
  await setDoc(doc(db, 'users', uid, 'meal_plans', 'ingredients'), {
    byDay,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

// ── Grocery lists ────────────────────────────────────────────────────────────

export async function fsGetGroceryList(uid, weekStartDate) {
  if (!uid || !db) return null;
  const snap = await getDoc(doc(db, 'users', uid, 'grocery_lists', weekStartDate));
  if (!snap.exists()) return {};
  const checked = snap.data().checked;
  return checked && typeof checked === 'object' ? checked : {};
}

export async function fsSaveGroceryList(uid, weekStartDate, checked) {
  if (!uid || !db) return;
  await setDoc(doc(db, 'users', uid, 'grocery_lists', weekStartDate), {
    checked,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

// ── Theme preference ─────────────────────────────────────────────────────────

export async function fsGetThemePref(uid) {
  if (!uid || !db) return null;
  const snap = await getDoc(doc(db, 'users', uid, 'preferences', 'root'));
  if (!snap.exists()) return null;
  return snap.data().theme || null;
}

export async function fsSaveThemePref(uid, theme) {
  if (!uid || !db) return;
  await setDoc(doc(db, 'users', uid, 'preferences', 'root'), { theme }, { merge: true });
}

// ── Gamification ─────────────────────────────────────────────────────────────

export async function fsGetUnlockedAchievements(uid) {
  if (!uid || !db) return null;
  const snap = await getDocs(collection(db, 'users', uid, 'unlocked_achievements'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function fsMarkAchievementUnlocked(uid, achievementId) {
  if (!uid || !db) return;
  await setDoc(doc(db, 'users', uid, 'unlocked_achievements', achievementId), {
    unlockedAt: new Date().toISOString(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

// ── Sleep ─────────────────────────────────────────────────────────────────────

export async function fsGetSleep(uid, date) {
  if (!uid || !db) return null;
  const snap = await getDoc(doc(db, 'users', uid, 'sleep', date));
  return snap.exists() ? snap.data() : null;
}

export async function fsLogSleep(uid, date, hours, quality) {
  if (!uid || !db) return;
  const safeHours = Math.min(24, Math.max(0, Number(hours) || 0));
  await setDoc(doc(db, 'users', uid, 'sleep', date), {
    hours: safeHours,
    quality: quality || null,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function fsDeleteSleep(uid, date) {
  if (!uid || !db) return;
  await deleteDoc(doc(db, 'users', uid, 'sleep', date));
}

// ── Habits ────────────────────────────────────────────────────────────────────

export async function fsGetHabits(uid) {
  if (!uid || !db) return null;
  const snap = await getDocs(collection(db, 'users', uid, 'habits'));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function fsSaveHabit(uid, habit) {
  if (!uid || !db) return null;
  const id = habit.id || `habit_${Date.now()}`;
  const entry = {
    ...habit,
    id,
    createdAt: habit.createdAt || new Date().toISOString(),
    active: habit.active !== false,
  };
  await setDoc(doc(db, 'users', uid, 'habits', id), entry);
  return entry;
}

export async function fsDeleteHabit(uid, id) {
  if (!uid || !db) return;
  await deleteDoc(doc(db, 'users', uid, 'habits', id));
}

export async function fsGetAllHabitLogs(uid) {
  if (!uid || !db) return null;
  const snap = await getDocs(collection(db, 'users', uid, 'habit_logs'));
  return snap.docs.map((d) => ({ date: d.id, ...d.data() }));
}

export async function fsToggleHabitLog(uid, date, habitId) {
  if (!uid || !db) return null;
  const ref = doc(db, 'users', uid, 'habit_logs', date);
  const snap = await getDoc(ref);
  const data = snap.exists() ? snap.data() : {};
  const next = { ...data, [habitId]: !data[habitId] };
  await setDoc(ref, next, { merge: true });
  return next;
}

// ── Streak freeze (shared preferences) ────────────────────────────────────────

export async function fsGetStreakFreeze(uid) {
  if (!uid || !db) return null;
  const snap = await getDoc(doc(db, 'users', uid, 'preferences', 'health'));
  const d = snap.exists() ? snap.data() : {};
  return {
    freezesAvailable: Number.isFinite(d.streakFreezesAvailable) ? d.streakFreezesAvailable : 2,
    frozenDates: Array.isArray(d.frozenDates) ? d.frozenDates : [],
  };
}

export async function fsSaveStreakFreeze(uid, { freezesAvailable, frozenDates }) {
  if (!uid || !db) return;
  await setDoc(doc(db, 'users', uid, 'preferences', 'health'), {
    streakFreezesAvailable: freezesAvailable,
    frozenDates: (frozenDates || []).slice(-60),
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export async function fsGetWeekSleep(uid) {
  if (!uid || !db) return null;
  const dates = localDateKeys(7).map((date) => ({
    date,
    day: new Date(`${date}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'short' }),
  }));
  const snaps = await Promise.all(
    dates.map(({ date }) => getDoc(doc(db, 'users', uid, 'sleep', date)))
  );
  return dates.map(({ date, day }, i) => {
    const data = snaps[i].exists() ? snaps[i].data() : {};
    return { date, day, hours: data.hours ?? 0, quality: data.quality ?? null };
  });
}
